import { useTranslate } from "@core/hooks"
import { Icon, type IconKey } from "@core/components"

type SubscriptionBannerProps = {
  variant:
    | "expired"
    | "grace_period"
    | "warning"
    | "warning_critical"
    | "warning_today"
    | "licenses_warning"
    | "licenses_critical"
  expiredDate?: string
  daysRemaining?: number
  licensesAvailable?: number
}

export const SubscriptionBanner = ({
  variant,
  expiredDate,
  daysRemaining,
  licensesAvailable,
}: SubscriptionBannerProps) => {
  const { t } = useTranslate("workspace")

  const formattedDate = expiredDate
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(expiredDate))
    : null

  const config: Record<
    SubscriptionBannerProps["variant"],
    {
      bg: string
      iconColor: string
      iconBg: string
      icon: IconKey
      message: string
      subMessage: string | null
    }
  > = {
    expired: {
      bg: "bg-accent-red-50",
      iconColor: "text-accent-red-800",
      iconBg: "bg-accent-red-50",
      icon: "circle-alert",
      message: t("subscription.banner.expired.message", { date: formattedDate }),
      subMessage: null,
    },
    grace_period: {
      bg: "bg-accent-red-50",
      iconColor: "text-accent-red-800",
      iconBg: "bg-accent-red-50",
      icon: "circle-alert",
      message: t("subscription.banner.grace-period.message", { date: formattedDate }),
      subMessage:
        daysRemaining !== undefined
          ? t("subscription.banner.grace-period.sub-message", { count: daysRemaining })
          : null,
    },
    warning_critical: {
      bg: "bg-accent-red-50",
      iconColor: "text-accent-red-800",
      iconBg: "bg-accent-red-50",
      icon: "clock",
      message: t("subscription.banner.warning-critical.message", { count: daysRemaining }),
      subMessage: null,
    },
    warning_today: {
      bg: "bg-accent-red-50",
      iconColor: "text-accent-red-800",
      iconBg: "bg-accent-red-50",
      icon: "clock",
      message: t("subscription.banner.warning-today.message"),
      subMessage: null,
    },
    warning: {
      bg: "bg-accent-yellow-50",
      iconColor: "text-accent-yellow-600",
      iconBg: "bg-accent-yellow-50",
      icon: "clock",
      message: t("subscription.banner.warning.message", { count: daysRemaining }),
      subMessage: null,
    },
    licenses_critical: {
      bg: "bg-accent-red-50",
      iconColor: "text-accent-red-800",
      iconBg: "bg-accent-red-50",
      icon: "users",
      message:
        licensesAvailable === 0
          ? t("subscription.banner.licenses-critical.message-zero")
          : t("subscription.banner.licenses-critical.message", { count: licensesAvailable }),
      subMessage: null,
    },
    licenses_warning: {
      bg: "bg-accent-yellow-50",
      iconColor: "text-accent-yellow-600",
      iconBg: "bg-accent-yellow-50",
      icon: "users",
      message: t("subscription.banner.licenses-warning.message", { count: licensesAvailable }),
      subMessage: null,
    },
  }

  const { bg, iconColor, iconBg, icon, message, subMessage } = config[variant]

  return (
    <div
      className={`flex items-center justify-between ${bg} rounded-md px-4 py-3`}
    >
      <div className="flex items-center gap-2">
        <div
          className={`flex items-center justify-center ${iconBg} rounded-lg shrink-0`}
        >
          <Icon
            name={icon}
            className={`size-[18px] ${iconColor}`}
            strokeWidth={1.6}
          />
        </div>
        <div className="flex flex-col gap-2 py-1.5">
          <p className="text-sm font-medium text-secondary-900 leading-[1.2]">
            {message}
          </p>
          {subMessage && (
            <p className="text-xl font-medium text-secondary-800 leading-normal">
              {subMessage}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        className="shrink-0 h-[41px] px-3 bg-white border border-neutral-200 rounded-lg text-sm font-medium text-secondary-900 hover:bg-neutral-50 transition-colors whitespace-nowrap"
        onClick={() => { window.location.href = `mailto:${import.meta.env.VITE_SALES_EMAIL}` }}
      >
        {t("subscription.banner.action")}
      </button>
    </div>
  )
}
